import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShoppingCart, Minus, Plus, Trash2, ArrowLeft, Home, TreePine, ChevronRight } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import SEOHead from "@/components/SEOHead";
import api, { formatPrice } from "@/lib/api";

export default function OrderPage() {
  const { t, lang } = useLanguage();
  const n = (item, fallback) => lang === "ru" ? (item.name_ru || item.name_kz || item.name || fallback) : (item.name_kz || item.name_ru || item.name || fallback);
  const cat = (item) => lang === "ru" ? (item.category_ru || item.category_kz || item.category || "") : (item.category_kz || item.category_ru || item.category || "");

  // State
  const [zoneType, setZoneType] = useState(null); // null | "house" | "letnik"
  const [letnikType, setLetnikType] = useState(null); // null | "tapshan" | "table"
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [houses, setHouses] = useState([]);
  const [venues, setVenues] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState("menu");
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get("/houses").then(r => setHouses(r.data)).catch(() => {});
    api.get("/venues").then(r => setVenues(r.data)).catch(() => {});
    api.get("/menu").then(r => setMenuItems(r.data)).catch(() => {});
    api.get("/services").then(r => setServices(r.data.filter(s => s.is_active !== false))).catch(() => {});
  }, []);

  const tapshans = venues.filter(v => v.venue_type === "tapshan");
  const tables = venues.filter(v => v.venue_type === "table");

  const addToCart = (item, type) => {
    const existing = cart.find(c => c.id === item.id && c._item_type === type);
    if (existing) {
      setCart(cart.map(c => (c.id === item.id && c._item_type === type) ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { ...item, _item_type: type, quantity: 1 }]);
    }
  };

  const updateQty = (id, type, delta) => {
    setCart(cart.map(c => (c.id === id && c._item_type === type) ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter(c => c.quantity > 0));
  };

  const cartTotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);

  const handleSubmit = async () => {
    if (!form.name || !form.phone) return alert(lang === "ru" ? "Заполните имя и телефон" : "Атыңызды және телефоныңызды жазыңыз");
    setSubmitting(true);
    try {
      const payload = {
        house_id: zoneType === "house" ? selectedPlace.id : "",
        venue_type: zoneType === "house" ? "house" : letnikType,
        venue_id: zoneType === "house" ? selectedPlace.id : selectedPlace.id,
        venue_name: n(selectedPlace, ""),
        items: cart.map(c => {
          const base = { name: n(c, ""), price: c.price, quantity: c.quantity, item_type: c._item_type };
          if (c._item_type === "menu") base.menu_item_id = c.id;
          else base.service_id = c.id;
          return base;
        }),
        client_name: form.name,
        client_phone: form.phone,
        notes: form.notes
      };
      await api.post("/orders", payload);
      setSuccess(true);
      setCart([]);
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    }
    setSubmitting(false);
  };

  const goBack = () => {
    if (showCart) { setShowCart(false); return; }
    if (selectedPlace) { setSelectedPlace(null); return; }
    if (letnikType) { setLetnikType(null); return; }
    if (zoneType) { setZoneType(null); return; }
  };

  // Categories
  const menuCategories = [...new Set(menuItems.map(i => cat(i)).filter(Boolean))];
  const serviceCategories = [...new Set(services.map(s => {
    const labels = { peeling: lang === "ru" ? "Пилинг" : "Пилинг", steaming: lang === "ru" ? "Парение" : "Парлау", massage: lang === "ru" ? "Массаж" : "Массаж", spa_program: lang === "ru" ? "СПА программы" : "СПА бағдарламалар", package: lang === "ru" ? "Пакеты" : "Пакеттер" };
    return labels[s.category] || s.category;
  }))];
  const [selectedCat, setSelectedCat] = useState("");

  const filteredMenu = selectedCat ? menuItems.filter(i => cat(i) === selectedCat) : menuItems;
  const filteredServices = selectedCat ? services.filter(s => {
    const labels = { peeling: lang === "ru" ? "Пилинг" : "Пилинг", steaming: lang === "ru" ? "Парение" : "Парлау", massage: lang === "ru" ? "Массаж" : "Массаж", spa_program: lang === "ru" ? "СПА программы" : "СПА бағдарламалар", package: lang === "ru" ? "Пакеты" : "Пакеттер" };
    return (labels[s.category] || s.category) === selectedCat;
  }) : services;

  if (success) {
    return (
      <div className="py-24 md:py-32" data-testid="order-success">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-serif text-3xl mb-4">{t("order.orderSuccess")}</h1>
          <p className="text-muted-foreground mb-8">{t("order.orderSuccessDesc")}</p>
          <Button onClick={() => { setSuccess(false); setZoneType(null); setSelectedPlace(null); setLetnikType(null); }} className="bg-primary text-primary-foreground rounded-none">
            {lang === "ru" ? "Новый заказ" : "Жаңа тапсырыс"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32" data-testid="order-page">
      <SEOHead path="/order" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {(zoneType || showCart) && (
              <button onClick={goBack} className="text-muted-foreground hover:text-foreground" data-testid="order-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h1 className="font-serif text-3xl md:text-4xl">{t("order.title")}</h1>
          </div>
          {cart.length > 0 && !showCart && selectedPlace && (
            <Button onClick={() => setShowCart(true)} className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="cart-btn">
              <ShoppingCart className="w-4 h-4" />
              {cartCount} — {formatPrice(cartTotal)}
            </Button>
          )}
        </div>

        {/* Step 1: Choose zone */}
        {!zoneType && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl" data-testid="zone-selection">
            <button
              onClick={() => setZoneType("house")}
              className="bg-card border border-white/10 p-8 text-left hover:border-primary/40 transition-colors group"
              data-testid="zone-house"
            >
              <Home className="w-10 h-10 text-primary mb-4" />
              <h2 className="font-serif text-xl mb-2">{lang === "ru" ? "Банные дома" : "Монша үйлері"}</h2>
              <p className="text-sm text-muted-foreground">{lang === "ru" ? "Заказ еды и услуг в банный дом" : "Монша үйіне тамақ және қызмет тапсырысы"}</p>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary mt-4 transition-colors" />
            </button>
            <button
              onClick={() => setZoneType("letnik")}
              className="bg-card border border-white/10 p-8 text-left hover:border-primary/40 transition-colors group"
              data-testid="zone-letnik"
            >
              <TreePine className="w-10 h-10 text-primary mb-4" />
              <h2 className="font-serif text-xl mb-2">{lang === "ru" ? "Летник" : "Жазғы алаң"}</h2>
              <p className="text-sm text-muted-foreground">{lang === "ru" ? "Тапчаны и столики на открытом воздухе" : "Тапшандар және үстелдер ашық аспан астында"}</p>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary mt-4 transition-colors" />
            </button>
          </div>
        )}

        {/* Step 1b: Choose letnik type */}
        {zoneType === "letnik" && !letnikType && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl" data-testid="letnik-type-selection">
            <button
              onClick={() => setLetnikType("tapshan")}
              className="bg-card border border-white/10 p-6 text-left hover:border-primary/40 transition-colors"
              data-testid="letnik-tapshan"
            >
              <h2 className="font-serif text-lg mb-1">{lang === "ru" ? "Тапчаны" : "Тапшандар"}</h2>
              <p className="text-sm text-muted-foreground">{tapshans.length} {lang === "ru" ? "шт." : "дана"}</p>
            </button>
            <button
              onClick={() => setLetnikType("table")}
              className="bg-card border border-white/10 p-6 text-left hover:border-primary/40 transition-colors"
              data-testid="letnik-table"
            >
              <h2 className="font-serif text-lg mb-1">{lang === "ru" ? "Столики" : "Үстелдер"}</h2>
              <p className="text-sm text-muted-foreground">{tables.length} {lang === "ru" ? "шт." : "дана"}</p>
            </button>
          </div>
        )}

        {/* Step 2: Choose specific place */}
        {zoneType === "house" && !selectedPlace && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="house-selection">
            {houses.map(h => (
              <button key={h.id} onClick={() => setSelectedPlace(h)} className="bg-card border border-white/10 overflow-hidden hover:border-primary/40 transition-colors" data-testid={`order-house-${h.id}`}>
                {h.photos?.[0] && <img src={h.photos[0]} alt="" className="w-full aspect-[16/10] object-cover" />}
                <div className="p-3 text-left">
                  <p className="font-serif text-sm">{n(h, "")}</p>
                  <p className="text-xs text-primary mt-1">{formatPrice(h.price_per_hour)}/{lang === "ru" ? "час" : "сағ"}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {zoneType === "letnik" && letnikType && !selectedPlace && (
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3" data-testid="venue-selection">
            {(letnikType === "tapshan" ? tapshans : tables).map(v => (
              <button key={v.id} onClick={() => setSelectedPlace(v)} className="bg-card border border-white/10 p-4 text-center hover:border-primary/40 transition-colors" data-testid={`order-venue-${v.id}`}>
                <p className="font-serif text-sm">{n(v, "")}</p>
              </button>
            ))}
          </div>
        )}

        {/* Step 3: Menu (after place selected) */}
        {selectedPlace && !showCart && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs text-primary uppercase tracking-widest">{n(selectedPlace, "")}</span>
            </div>

            {/* Tabs: only show services tab for houses */}
            {zoneType === "house" ? (
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedCat(""); }} className="mb-6">
                <TabsList className="bg-card border border-white/10 rounded-none">
                  <TabsTrigger value="menu" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-menu">
                    {t("order.menuTab")}
                  </TabsTrigger>
                  <TabsTrigger value="services" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-services">
                    {t("order.servicesTab")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            ) : (
              <div className="mb-6">
                <span className="text-sm text-muted-foreground">{lang === "ru" ? "Меню" : "Мәзір"}</span>
              </div>
            )}

            {/* Categories */}
            {((activeTab === "menu" || zoneType !== "house") ? menuCategories : serviceCategories).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                <button onClick={() => setSelectedCat("")} className={`px-3 py-1 text-xs border transition-colors ${!selectedCat ? "bg-primary text-primary-foreground border-primary" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
                  {lang === "ru" ? "Все" : "Барлығы"}
                </button>
                {((activeTab === "menu" || zoneType !== "house") ? menuCategories : serviceCategories).map(c => (
                  <button key={c} onClick={() => setSelectedCat(c)} className={`px-3 py-1 text-xs border transition-colors ${selectedCat === c ? "bg-primary text-primary-foreground border-primary" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}

            {/* Items grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {((activeTab === "menu" || zoneType !== "house") ? filteredMenu : filteredServices).map(item => {
                const itemType = (activeTab === "menu" || zoneType !== "house") ? "menu" : "service";
                const inCart = cart.find(c => c.id === item.id && c._item_type === itemType);
                return (
                  <div key={`${itemType}-${item.id}`} className="bg-card border border-white/10 p-4 flex items-center justify-between" data-testid={`menu-item-${item.id}`}>
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="font-medium text-sm truncate">{n(item, "")}</p>
                      <p className="text-primary text-sm mt-0.5">{formatPrice(item.price)}</p>
                    </div>
                    {inCart ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(item.id, itemType, -1)} className="w-7 h-7 border border-white/10 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <span className="text-sm w-5 text-center">{inCart.quantity}</span>
                        <button onClick={() => updateQty(item.id, itemType, 1)} className="w-7 h-7 border border-white/10 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <Button size="sm" onClick={() => addToCart(item, itemType)} className="bg-primary/10 text-primary hover:bg-primary/20 rounded-none text-xs" data-testid={`add-${item.id}`}>
                        <Plus className="w-3 h-3 mr-1" /> {t("order.addToCart")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cart / Checkout */}
        {showCart && (
          <div className="max-w-xl" data-testid="cart-view">
            <h2 className="font-serif text-2xl mb-6">{t("order.yourCart")} — {n(selectedPlace, "")}</h2>
            <div className="space-y-3 mb-6">
              {cart.map(c => (
                <div key={`${c._item_type}-${c.id}`} className="bg-card border border-white/10 p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium truncate">{n(c, "")}</p>
                    <p className="text-xs text-muted-foreground">{formatPrice(c.price)} x {c.quantity}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(c.id, c._item_type, -1)} className="w-7 h-7 border border-white/10 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <span className="text-sm w-5 text-center">{c.quantity}</span>
                    <button onClick={() => updateQty(c.id, c._item_type, 1)} className="w-7 h-7 border border-white/10 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                    <button onClick={() => setCart(cart.filter(x => !(x.id === c.id && x._item_type === c._item_type)))} className="text-red-400 hover:text-red-300 ml-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-card border border-primary/30 p-4 mb-6">
              <div className="flex justify-between text-lg font-serif">
                <span>{lang === "ru" ? "Итого" : "Барлығы"}</span>
                <span className="text-primary">{formatPrice(cartTotal)}</span>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              <Input placeholder={t("booking.yourName")} value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-background border-white/10 rounded-none" data-testid="order-name" />
              <Input placeholder={t("booking.yourPhone")} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-background border-white/10 rounded-none" data-testid="order-phone" />
              <Input placeholder={t("booking.notes")} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-background border-white/10 rounded-none" data-testid="order-notes" />
            </div>
            <Button onClick={handleSubmit} disabled={submitting || cart.length === 0} className="w-full bg-primary text-primary-foreground rounded-none py-6" data-testid="order-submit">
              {submitting ? t("booking.submitting") : t("order.checkout")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
